import { useI18n } from "#hooks/use-i18n"
import type { ImportProgress as ImportProgressType } from "#lib/indexeddb-import-store"
import { Dialog, DialogActions } from "../Dialog"
import { Badge } from "../ui/badge"
import { Button } from "../ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Progress } from "../ui/progress"

interface ImportProgressProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  progress: ImportProgressType
  onPause?: () => void
  onResume?: () => void
  onCancel?: () => void
  onGoBack?: () => void
}

export function ImportProgress({
  open,
  onOpenChange,
  progress,
  onPause,
  onResume,
  onCancel,
  onGoBack,
}: ImportProgressProps) {
  const { t } = useI18n()
  const progressPercentage =
    progress.totalRows > 0 ? (progress.processedRows / progress.totalRows) * 100 : 0

  const isPaused = progress.status === "paused"
  const isCompleted = progress.status === "completed"
  const isFailed = progress.status === "failed"

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("components.expenseImport.importingTitle")}
      description={t("components.expenseImport.importingDescription")}
      size="lg"
    >
      <div className="space-y-6 pb-6">
        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{t("components.expenseImport.progress")}</span>
            <span className="font-medium">
              {t("components.expenseImport.rows", {
                processed: progress.processedRows,
                total: progress.totalRows,
              })}
            </span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
          <div className="text-xs text-muted-foreground text-center">
            {t("components.expenseImport.complete", {
              percentage: progressPercentage.toFixed(1),
            })}
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-normal text-muted-foreground">
                {t("components.expenseImport.successful")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {progress.successfulRows}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-normal text-muted-foreground">
                {t("components.expenseImport.failed")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-destructive">{progress.failedRows}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-normal text-muted-foreground">
                {t("components.expenseImport.status")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Badge
                variant={
                  isCompleted
                    ? "default"
                    : isFailed
                      ? "destructive"
                      : isPaused
                        ? "secondary"
                        : "default"
                }
              >
                {progress.status === "completed"
                  ? t("components.expenseImport.completed")
                  : progress.status === "failed"
                    ? t("components.expenseImport.failed")
                    : progress.status === "paused"
                      ? t("components.expenseImport.paused")
                      : t("components.expenseImport.inProgress")}
              </Badge>
            </CardContent>
          </Card>
        </div>

        {/* Errors */}
        {progress.errors.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">
                  {t("components.expenseImport.errors", {
                    count: progress.errors.length,
                  })}
                </CardTitle>
                {onGoBack && (isCompleted || isFailed) && (
                  <Button type="button" variant="outline" size="sm" onClick={onGoBack}>
                    {t("components.expenseImport.goBackToFix")}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {progress.errors.slice(0, 20).map((error, index) => {
                  const isCategoryError = error.index === -1
                  return (
                    <div
                      key={index}
                      className="text-sm p-3 rounded bg-destructive/10 border border-destructive/20"
                    >
                      <div className="font-medium mb-1">
                        {isCategoryError ? (
                          <span className="flex items-center gap-2">
                            <span>{t("components.expenseImport.categoryError")}</span>
                            <span className="text-xs font-normal text-muted-foreground">
                              {t("components.expenseImport.affectsAllRows")}
                            </span>
                          </span>
                        ) : (
                          t("components.expenseImport.row", {
                            index: error.index + 1,
                          })
                        )}
                      </div>
                      <div className="text-muted-foreground text-xs whitespace-pre-wrap break-words">
                        {error.error}
                      </div>
                      {error.row && (
                        <div className="mt-2 pt-2 border-t border-destructive/20">
                          <div className="text-xs text-muted-foreground">
                            <div className="font-medium mb-1">
                              {isCategoryError
                                ? t("components.expenseImport.category")
                                : t("components.expenseImport.rowData")}
                            </div>
                            <div className="space-y-0.5">
                              {isCategoryError ? (
                                error.row.categoryName && (
                                  <div>
                                    {t("components.expenseImport.categoryName")}:{" "}
                                    {error.row.categoryName}
                                  </div>
                                )
                              ) : (
                                <>
                                  {error.row.name && (
                                    <div>
                                      {t("components.expenseImport.name")}: {error.row.name}
                                    </div>
                                  )}
                                  {error.row.amount !== undefined && (
                                    <div>
                                      {t("components.expenseImport.amount")}: {error.row.amount}
                                    </div>
                                  )}
                                  {error.row.date && (
                                    <div>
                                      {t("components.expenseImport.date")}: {error.row.date}
                                    </div>
                                  )}
                                  {error.row.expenseCategoryId && (
                                    <div>
                                      {t("components.expenseImport.categoryId")}:{" "}
                                      {error.row.expenseCategoryId}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
                {progress.errors.length > 20 && (
                  <div className="text-xs text-muted-foreground text-center pt-2">
                    {t("components.expenseImport.moreErrors", {
                      count: progress.errors.length - 20,
                    })}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        {!isCompleted && !isFailed && (
          <div className="flex gap-2">
            {isPaused ? (
              <Button type="button" variant="default" onClick={onResume} className="flex-1">
                {t("components.expenseImport.resumeImport")}
              </Button>
            ) : (
              <Button type="button" variant="outline" onClick={onPause} className="flex-1">
                {t("components.expenseImport.pauseImport")}
              </Button>
            )}
            <Button type="button" variant="destructive" onClick={onCancel} className="flex-1">
              {t("components.expenseImport.cancelImport")}
            </Button>
          </div>
        )}
      </div>

      {(isCompleted || isFailed) && (
        <DialogActions
          onCancel={
            onGoBack
              ? () => {
                  onOpenChange(false)
                  onGoBack()
                }
              : () => onOpenChange(false)
          }
          onConfirm={
            isCompleted
              ? () => onOpenChange(false)
              : onGoBack
                ? () => {
                    onOpenChange(false)
                    onGoBack()
                  }
                : undefined
          }
          confirmText={
            isCompleted
              ? t("components.expenseImport.close")
              : onGoBack
                ? t("components.expenseImport.goBackToFix")
                : t("components.expenseImport.close")
          }
          cancelText={
            onGoBack
              ? t("components.expenseImport.back")
              : isCompleted
                ? undefined
                : t("components.expenseImport.close")
          }
        />
      )}
    </Dialog>
  )
}
